import { Sidebar } from '@components/dashboard/superuser/Sidebar';
import { SETTINGS_SIDEBAR_SECTIONS } from '@components/dashboard/superuser/sidebarConfig';
import { useAppSelector } from '@hooks/useRedux';
import { formatShortDate } from '@/renderer/lib/utils';
import { AccessControlCode } from '@shared/constants';
import {
  selectAccessControlCodes,
  selectBillingRecoveryDates,
  selectCurrentUser,
  selectHasUnrestrictedAccess,
  selectIsSubscriptionInGracePeriod,
  selectIsSubscriptionSuspended,
} from '@store/auth/authSelectors';
import { Settings as SettingsIcon } from 'lucide-react';
import React, { useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { OutletSelector } from '../components/common';
import { AlertCard } from '../components/common/AlertCard';
import { UserActionsMenu } from '../components/nav/UserActionsMenu';
import { BILLING_PAGE_PATH } from '../util/renderConstants';

// Printers and Billing moved here from the dashboard, so their access codes
// also grant entry to the Settings shell.
const SETTINGS_ENTRY_CODES: string[] = [
  AccessControlCode.DASHBOARD_SECTIONS_TABLES_MANAGE,
  AccessControlCode.DASHBOARD_BILLINGS_ACCESS,
];

export const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUser = useAppSelector(selectCurrentUser);
  const hasUnrestrictedAccess = useAppSelector(selectHasUnrestrictedAccess);
  const accessControlCodes = useAppSelector(selectAccessControlCodes);
  const canAccess = Boolean(
    hasUnrestrictedAccess ||
    accessControlCodes.some(
      (code) =>
        code.startsWith('settings.') || SETTINGS_ENTRY_CODES.includes(code),
    ),
  );
  const isSubscriptionInGracePeriod = useAppSelector(
    selectIsSubscriptionInGracePeriod,
  );
  const isSubscriptionSuspended = useAppSelector(selectIsSubscriptionSuspended);
  const billingRecoveryDates = useAppSelector(selectBillingRecoveryDates);
  const isBillingPage = location.pathname === BILLING_PAGE_PATH;
  // While suspended, Billing is the only Settings page that stays usable —
  // it's how the workspace gets restored.
  const shouldLockContent = isSubscriptionSuspended && !isBillingPage;
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!canAccess) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <SettingsIcon size={32} className="mx-auto mb-3 text-gray-300" />
          <p className="text-sm font-medium text-gray-600">Access restricted</p>
          <p className="mt-1 text-xs text-gray-400">
            You do not have permission to access settings.
          </p>
          <button
            type="button"
            onClick={() => navigate('/sales')}
            className="mt-4 text-xs text-pos-primary underline"
          >
            Back to sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50 font-sans text-sm text-gray-900">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed((v) => !v)}
        sections={SETTINGS_SIDEBAR_SECTIONS}
      />

      <div className="flex flex-1 flex-col overflow-hidden">
        <header className="flex items-center justify-between border-b border-gray-100 bg-white px-8 py-4">
          <div className="flex items-center gap-4">
            <SettingsIcon size={32} className="text-pos-primary" />
            <div className="flex-1">
              <h1 className="text-base font-semibold text-gray-900">
                Settings
              </h1>
              <p className="text-xs text-gray-400">
                Users, security, printers, billing, notifications, and fiscal
                device configuration.
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <OutletSelector />
            <UserActionsMenu />
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-7xl space-y-4">
            {isSubscriptionInGracePeriod && !isBillingPage ? (
              <AlertCard
                title="Billing grace period active"
                description={`Payment is still pending for this workspace. Please renew before ${billingRecoveryDates.gracePeriodEndsAt ? formatShortDate(billingRecoveryDates.gracePeriodEndsAt) : 'the grace window ends'} to avoid service interruption.`}
                buttonLink={BILLING_PAGE_PATH}
                buttonText="Review billing"
              />
            ) : null}

            {shouldLockContent ? (
              <AlertCard
                title="Subscription suspended until billing is restored"
                description="This workspace is currently in billing recovery mode. Settings remain visible for reference, but changes are locked until the subscription is renewed."
                info={`Data retention is scheduled until ${billingRecoveryDates.dataPurgeScheduledFor ? formatShortDate(billingRecoveryDates.dataPurgeScheduledFor) : ' a later recovery date'}`}
                buttonLink={BILLING_PAGE_PATH}
                buttonText="Restore billing"
              />
            ) : null}

            <div
              className={
                shouldLockContent
                  ? 'pointer-events-none opacity-45 saturate-50'
                  : ''
              }
            >
              <Outlet />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default SettingsPage;
